import React, {
  createContext,
  useContext,
  useCallback,
  useRef,
  useEffect,
} from 'react';
import { useForm, UseFormReturn, useWatch } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { proposalFormSchema } from '@/constants/healthInsuranceConfig';
import { BANK_DETAILS_FIELDS } from '@/constants/fieldDescriptors/bankDetailsFields';
import { NOMINEE_FIELDS } from '@/constants/fieldDescriptors/nomineeFields';
import { PROPOSER_FIELDS } from '@/constants/fieldDescriptors/proposerFields';
import { buildFieldGroupSchema } from '@/utils/buildFieldGroupSchema';
import {
  bankDetailsDescriptorSuperRefine,
  nomineeDescriptorSuperRefine,
  proposerDescriptorSuperRefine,
} from '@/utils/fieldDescriptorSchemaRefines';
import { z } from 'zod';
import { useFormPersist } from '@/hooks/useFormPersist';
import { useRhiFlowStore } from '@/store/rhiFlowStore';
// Type definitions for each form
const nomineeFormSchema = buildFieldGroupSchema(NOMINEE_FIELDS, {
  superRefine: nomineeDescriptorSuperRefine,
});

const proposerFormSchema = buildFieldGroupSchema(PROPOSER_FIELDS, {
  superRefine: proposerDescriptorSuperRefine,
});

const bankDetailsFormSchema = buildFieldGroupSchema(BANK_DETAILS_FIELDS, {
  superRefine: bankDetailsDescriptorSuperRefine,
});

export type ProposalFormData = z.infer<typeof proposalFormSchema>;
export type ProposerFormData = z.infer<typeof proposerFormSchema>;
export type NomineeFormData = z.infer<typeof nomineeFormSchema>;
export type BankDetailsFormData = z.infer<typeof bankDetailsFormSchema>;

const defaultProposalFormValues: ProposalFormData = {
  members: [],
};

const defaultProposerFormValues: ProposerFormData = {
  name: '',
  gender: 'MALE',
  dateOfBirth: '',
  email: '',
  phoneNumber: '',
  aadharNumber: '',
  panNumber: '',
};

const defaultNomineeFormValues: NomineeFormData = {
  name: '',
  dateOfBirth: '',
  relationship: '' as NomineeFormData['relationship'],
};

const defaultBankDetailsFormValues: BankDetailsFormData = {
  nameAsInBankAccount: '',
  bankName: '',
  bankAccountNumber: '',
  confirmBankAccountNumber: '',
  ifscCode: '',
};

const getInitialFormValues = <T,>(defaults: T, snapshot?: T): T => ({
  ...defaults,
  ...snapshot,
});

// Context interface
interface HealthInsuranceFormsContextType {
  proposalForm: UseFormReturn<ProposalFormData>;
  proposerForm: UseFormReturn<ProposerFormData>;
  nomineeForm: UseFormReturn<NomineeFormData>;
  bankDetailsForm: UseFormReturn<BankDetailsFormData>;
}

// Form state snapshot interface
interface FormStateSnapshot {
  memberData: ProposalFormData['members'][number];
  timestamp: number;
}

// Form state manager interface
interface FormStateManager {
  saveCurrentState: () => void;
  resetToInitialState: () => void;
  clearFormData: () => void;
  hasFormChanged: () => boolean;
  getCurrentMemberData: () => ProposalFormData['members'][number] | null;
  isEditMode: () => boolean;
  memberIndex: number;
}

// Create the context
const HealthInsuranceFormsContext = createContext<
  HealthInsuranceFormsContextType | undefined
>(undefined);

// Provider component
export const HealthInsuranceFormsProvider: React.FC<{
  children: React.ReactNode;
}> = ({ children }) => {
  const formSnapshots = useRhiFlowStore((s) => s.formSnapshots);
  const setFormSnapshot = useRhiFlowStore((s) => s.setFormSnapshot);

  const proposalForm = useForm<ProposalFormData>({
    resolver: zodResolver(proposalFormSchema),
    defaultValues: getInitialFormValues(
      defaultProposalFormValues,
      formSnapshots.proposal,
    ),
    mode: 'onChange',
  });

  const proposerForm = useForm<ProposerFormData>({
    resolver: zodResolver(proposerFormSchema),
    defaultValues: getInitialFormValues(
      defaultProposerFormValues,
      formSnapshots.proposer
        ? {
            ...defaultProposerFormValues,
            ...formSnapshots.proposer,
          }
        : undefined,
    ),
    mode: 'onSubmit',
  });

  const nomineeForm = useForm<NomineeFormData>({
    resolver: zodResolver(nomineeFormSchema),
    defaultValues: getInitialFormValues(
      defaultNomineeFormValues,
      formSnapshots.nominee,
    ),
    mode: 'onSubmit',
  });

  const bankDetailsForm = useForm<BankDetailsFormData>({
    resolver: zodResolver(bankDetailsFormSchema),
    defaultValues: getInitialFormValues(
      defaultBankDetailsFormValues,
      formSnapshots.bankDetails,
    ),
    mode: 'onSubmit',
  });

  useFormPersist(proposalForm, {
    defaultValues: defaultProposalFormValues,
    snapshot: formSnapshots.proposal,
    onPersist: (data) => setFormSnapshot('proposal', data),
  });
  useFormPersist(proposerForm, {
    defaultValues: defaultProposerFormValues,
    snapshot: formSnapshots.proposer,
    onPersist: (data) => setFormSnapshot('proposer', data),
    excludeFields: ['aadharNumber', 'panNumber'],
  });
  useFormPersist(nomineeForm, {
    defaultValues: defaultNomineeFormValues,
    snapshot: formSnapshots.nominee,
    onPersist: (data) => setFormSnapshot('nominee', data),
  });
  useFormPersist(bankDetailsForm, {
    defaultValues: defaultBankDetailsFormValues,
    snapshot: formSnapshots.bankDetails,
    onPersist: (data) => setFormSnapshot('bankDetails', data),
  });

  return (
    <HealthInsuranceFormsContext.Provider
      value={{
        proposalForm,
        proposerForm,
        nomineeForm,
        bankDetailsForm,
      }}
    >
      {children}
    </HealthInsuranceFormsContext.Provider>
  );
};

// Custom hooks for each form
export const useProposalForm = () => {
  const context = useContext(HealthInsuranceFormsContext);
  if (!context) {
    throw new Error(
      'useProposalForm must be used within HealthInsuranceFormsProvider',
    );
  }
  return context.proposalForm;
};

export const useProposerForm = () => {
  const context = useContext(HealthInsuranceFormsContext);
  if (!context) {
    throw new Error(
      'useProposerForm must be used within HealthInsuranceFormsProvider',
    );
  }
  return context.proposerForm;
};

export const useNomineeForm = () => {
  const context = useContext(HealthInsuranceFormsContext);
  if (!context) {
    throw new Error(
      'useNomineeForm must be used within HealthInsuranceFormsProvider',
    );
  }
  return context.nomineeForm;
};

export const useBankDetailsForm = () => {
  const context = useContext(HealthInsuranceFormsContext);
  if (!context) {
    throw new Error(
      'useBankDetailsForm must be used within HealthInsuranceFormsProvider',
    );
  }
  return context.bankDetailsForm;
};

// Form state manager hook that works with the proposal form context
export const useFormStateManager = (relationship: string): FormStateManager => {
  const proposalForm = useProposalForm();
  const { control, setValue } = proposalForm;
  const initialStateRef = useRef<FormStateSnapshot | null>(null);
  const currentStateRef = useRef<FormStateSnapshot | null>(null);

  const members = useWatch<ProposalFormData, 'members'>({ control, name: 'members' }) ?? [];
  const memberIndex = members.findIndex((m) => m.relation === relationship);

  // Save initial state when component mounts or when editing existing member
  useEffect(() => {
    if (memberIndex >= 0 && !initialStateRef.current) {
      const currentMember = members[memberIndex];
      if (
        currentMember &&
        (currentMember.name || currentMember.answers?.length > 0)
      ) {
        initialStateRef.current = {
          memberData: JSON.parse(JSON.stringify(currentMember)),
          timestamp: Date.now(),
        };
      }
    }
  }, [memberIndex, members]);

  // Save current state periodically
  const saveCurrentState = useCallback(() => {
    if (memberIndex >= 0) {
      const currentMember = members[memberIndex];
      if (currentMember) {
        currentStateRef.current = {
          memberData: { ...currentMember },
          timestamp: Date.now(),
        };
      }
    }
  }, [memberIndex, members]);

  // Reset form to initial state (for back button or cancel)
  const resetToInitialState = useCallback(() => {
    if (memberIndex >= 0) {
      if (initialStateRef.current) {
        setValue(
          `members.${memberIndex}`,
          JSON.parse(JSON.stringify(initialStateRef.current.memberData)) as unknown as ProposalFormData['members'][number],
        );
      } else {
        // New member scenario - clear the member data but keep basic structure
        const emptyMember: ProposalFormData['members'][number] = {
          id: members[memberIndex]?.id || '',
          name: '',
          gender: '',
          dateOfBirth: '',
          heightFt: 0,
          heightInches: 0,
          weightKg: 0,
          answers: [],
          relation: relationship,
        };
        setValue(`members.${memberIndex}`, emptyMember);
      }
    }
  }, [memberIndex, members, relationship, setValue]);

  // Clear all form data (complete reset)
  const clearFormData = useCallback(() => {
    if (memberIndex >= 0) {
      const emptyMember: ProposalFormData['members'][number] = {
        id: members[memberIndex]?.id || '',
        name: '',
        gender: '',
        dateOfBirth: '',
        heightFt: 0,
        heightInches: 0,
        weightKg: 0,
        answers: [],
        relation: relationship,
      };
      setValue(`members.${memberIndex}`, emptyMember);
    }
  }, [memberIndex, members, relationship, setValue]);

  // Check if form has been modified from initial state
  const hasFormChanged = useCallback((): boolean => {
    if (memberIndex < 0) return false;

    const currentMember = members[memberIndex];
    if (!currentMember) return false;

    if (!initialStateRef.current) {
      // New member - check if any data has been entered
      return !!(
        currentMember.name ||
        currentMember.gender ||
        currentMember.dateOfBirth ||
        currentMember.heightFt ||
        currentMember.heightInches ||
        currentMember.weightKg ||
        (currentMember.answers && currentMember.answers.length > 0)
      );
    }

    // Edit scenario - compare with initial state
    const initial = initialStateRef.current.memberData;
    return (
      currentMember.name !== initial.name ||
      currentMember.gender !== initial.gender ||
      currentMember.dateOfBirth !== initial.dateOfBirth ||
      currentMember.heightFt !== initial.heightFt ||
      currentMember.heightInches !== initial.heightInches ||
      currentMember.weightKg !== initial.weightKg ||
      JSON.stringify(currentMember.answers) !== JSON.stringify(initial.answers)
    );
  }, [memberIndex, members]);

  // Get current member data
  const getCurrentMemberData = useCallback((): ProposalFormData['members'][number] | null => {
    if (memberIndex >= 0) {
      return members[memberIndex] || null;
    }
    return null;
  }, [memberIndex, members]);

  // Check if this is an edit scenario
  const isEditMode = useCallback((): boolean => {
    return !!initialStateRef.current;
  }, []);

  return {
    saveCurrentState,
    resetToInitialState,
    clearFormData,
    hasFormChanged,
    getCurrentMemberData,
    isEditMode,
    memberIndex,
  };
};
